import * as PopoverPrimitive from '@radix-ui/react-popover'
import { forwardRef } from 'react'
import { cn } from '@/lib/utils/cn'

/**
 * Thin Radix Popover wrapper. Two usage patterns:
 *
 *   <Popover>
 *     <PopoverTrigger>…</PopoverTrigger>
 *     <PopoverContent>
 *       custom content
 *     </PopoverContent>
 *   </Popover>
 *
 *   <Popover>
 *     <PopoverTrigger>…</PopoverTrigger>
 *     <PopoverMenu>
 *       <PopoverMenuGroup label="Actions">
 *         <PopoverMenuItem onSelect={…} shortcut="⌘E">Export</PopoverMenuItem>
 *       </PopoverMenuGroup>
 *       <PopoverMenuDivider />
 *       <PopoverMenuItem danger onSelect={…}>Delete</PopoverMenuItem>
 *     </PopoverMenu>
 *   </Popover>
 *
 * Dismisses on outside click, Esc, or item select (for the menu pattern).
 */
/* eslint-disable react-refresh/only-export-components */
// Direct re-exports of Radix primitives are components, but the plugin's
// heuristic flags const-exports. Safe to suppress — no state lives here.
export const Popover = PopoverPrimitive.Root
export const PopoverTrigger = PopoverPrimitive.Trigger
export const PopoverAnchor = PopoverPrimitive.Anchor
/* eslint-enable react-refresh/only-export-components */

export const PopoverContent = forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(function PopoverContent({ className, align = 'start', sideOffset = 6, ...props }, ref) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          'z-popover min-w-[180px] overflow-hidden rounded-md border border-border-default bg-surface text-primary shadow-md',
          'animate-in fade-in-0 zoom-in-95 duration-fast',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  )
})

/** Container for menu-style popover content. */
export const PopoverMenu = forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(function PopoverMenu({ className, align = 'start', sideOffset = 6, children, ...props }, ref) {
  return (
    <PopoverContent
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn('min-w-56 py-1', className)}
      {...props}
    >
      <div role="menu">{children}</div>
    </PopoverContent>
  )
})

export function PopoverMenuGroup({
  label,
  children,
}: {
  label?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div role="group">
      {label ? (
        <div className="text-role-section-label px-3 pt-1 pb-0.5">
          {label}
        </div>
      ) : null}
      {children}
    </div>
  )
}

export function PopoverMenuDivider() {
  return <div role="separator" aria-hidden="true" className="my-1 h-px bg-border-subtle" />
}

export interface PopoverMenuItemProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onSelect'> {
  /** Destructive intent — paints danger text. */
  danger?: boolean
  /** Right-aligned keyboard-shortcut chip. */
  shortcut?: string
  /** Optional leading icon. */
  icon?: React.ComponentType<{ className?: string; size?: number }>
  /** Called when the item is activated. Closing the popover is the consumer's job via controlled state. */
  onSelect?: () => void
}

export const PopoverMenuItem = forwardRef<HTMLButtonElement, PopoverMenuItemProps>(
  function PopoverMenuItem(
    { danger, shortcut, icon: Icon, onSelect, className, children, ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        role="menuitem"
        onClick={onSelect}
        className={cn(
          'flex w-full cursor-pointer items-center gap-2 border-none bg-transparent px-3 py-1.5 text-left text-sm text-primary',
          'hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none',
          danger && 'text-danger-text hover:bg-danger-bg',
          className,
        )}
        {...rest}
      >
        {Icon ? <Icon size={14} className={cn('flex-shrink-0', !danger && 'text-tertiary')} /> : null}
        <span className="flex-1 truncate">{children}</span>
        {shortcut ? (
          <span className="text-role-kbd-shortcut text-tertiary">{shortcut}</span>
        ) : null}
      </button>
    )
  },
)
